import React, { useEffect, useState, createRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import classnames from 'classnames';
import * as Yup from 'yup';

import { ScreenReaderOnly } from 'components/misc';
import { SecondaryButton, FormError } from '@huggg/react-components';

import dictionary from 'config/dictionary';
import styles from './form.module.scss';

const keyMap = {
  shift: 16,
  backspace: 8,
  tab: 9,
};

const Pin = (props) => {
  const {
    requiredValidationError = 'Pin please',
    button: ButtonComponent,
    errors: propsErrors,
    focusOnMount,
    onSubmit,
    pinLength,
    ...rest
  } = props;
  const inputRefs = [];
  const [firstMount, setFirstMount] = useState(true);

  const validationSchema = Yup.object().shape({
    pin: Yup.array()
      .of(Yup.number())
      .required(requiredValidationError)
      .min(pinLength),
  });

  for (let i = 0; i < pinLength; i++) {
    inputRefs[i] = createRef();
  }

  // TODO wtf is going on here? setCharAt?
  const onChange = (event, index, { setFieldValue }) => {
    setFieldValue(`pin[${index}]`, event.target.value.replace(/[^\d]+/g, ''));
  };

  const onPaste = (event, index, { setFieldValue }) => {
    if (index !== 0) {
      return;
    }

    const pastedData = event.clipboardData.getData('text/plain');

    if (pastedData.length !== pinLength) {
      return;
    }

    const pin = pastedData.split('');

    pin.forEach((char, index) => {
      setFieldValue(`pin[${index}]`, parseInt(char));
    });

    event.preventDefault();
  };

  const onFocus = (event, { setFieldError }) => {
    setFieldError('pin', null);

    if (event.target.value.length) {
      event.target.select();
      event.target.setSelectionRange(0, 10);
    }
  };

  const onKeyUp = (event, index) => {
    const code = event.which || event.keyCode;

    if (
      !event.shiftKey &&
      code !== keyMap.tab &&
      code !== keyMap.shift &&
      event.target.value
    ) {
      if (index !== inputRefs.length - 1) {
        inputRefs[index + 1].current.focus();
      }
    }

    if (code === keyMap.backspace && index !== 0) {
      inputRefs[index - 1].current.focus();
    }
  };

  const onFormSubmit = (values, formikBag) => {
    const pin = values.pin.join('');

    onSubmit({ pin }, formikBag);
  };

  useEffect(() => {
    if (firstMount) {
      focusOnMount && inputRefs[0].current.focus();
      setFirstMount(false);
    }
  }, [firstMount, focusOnMount, inputRefs, propsErrors]);

  const renderErrorMessage = (className, message) => (
    <div className={styles.errorContainer}>
      <FormError className={className}>{message}</FormError>
    </div>
  );

  const initialValues = useMemo(() => ({ pin: [] }), []);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onFormSubmit}
      validationSchema={validationSchema}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {(formikBag) => {
        const { values, errors, setFieldError, setFieldValue, touched } =
          formikBag;

        const hasError = touched.pin && errors.pin;

        return (
          <Form {...rest}>
            <ScreenReaderOnly
              srComponent={
                <label htmlFor="pin-number">
                  {dictionary.forms.pin.fields.pin.label}
                </label>
              }
            />

            {[...Array(pinLength)].map((_, index) => (
              <Field
                autoComplete="one-time-code"
                data-test-id="pin-input"
                type="tel"
                maxLength="1"
                id={`pin-${index}`}
                name={`pin[${index}]`}
                innerRef={inputRefs[index]}
                onChange={(event) => onChange(event, index, { setFieldValue })}
                onKeyUp={(event) => onKeyUp(event, index)}
                onFocus={(event) => onFocus(event, { setFieldError })}
                onPaste={(event) => onPaste(event, index, { setFieldValue })}
                value={values.pin[index]}
                key={index}
                className={classnames(
                  styles.input,
                  styles.inputPin,
                  styles.inputTextCenter,
                  { [styles.inputError]: errors.pin && touched.pin },
                )}
              />
            ))}

            {hasError && renderErrorMessage(styles.errorMessage, errors.pin)}

            {React.cloneElement(ButtonComponent, {
              ...ButtonComponent.props,
              type: 'submit',
              className: classnames(
                styles.button,
                ButtonComponent.props.className,
              ),
            })}
          </Form>
        );
      }}
    </Formik>
  );
};

Pin.defaultProps = {
  button: <SecondaryButton />,
  pinLength: 4,
};

Pin.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  button: PropTypes.element,
  errors: PropTypes.shape({
    pin: PropTypes.string,
  }),
  pinLength: PropTypes.number,
};

export default Pin;
